export interface BallInfo {
  boll: string;
  class: 'red' | 'blue' | 'green' | '0';
  title: string;
  sx: {
    id?: number;
    name: string;
    code?: string;
    wuxing: string;
  };
}

export interface DrawRecord {
  id?: number;
  game: string;
  period: number;
  numbers?: string;
  isopen: number;
  date?: string;
  format: BallInfo[];
  tm_num?: string;
  tm_dx?: string;
  tm_ds?: string;
  regular_count?: number;
  special_count?: number;
}

export type RecentDrawsResponse = Record<GameType, DrawRecord[]>;

export interface PlayType {
  id: number;
  title: string;
  tag: string;
  rule?: string;
  play_mode?: 'normal' | 'image' | 'image_text' | string;
  hit_effect_mode?: 'normal' | 'legacy' | string;
  background_image?: string;
  scene_width?: number;
  scene_height?: number;
  display_count?: number;
  predict_count?: number;
  prediction_rule?: 'hit' | 'exclude' | string;
  display_items?: Array<{
    enabled: boolean;
    name?: string;
    value: string;
    image?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    sort?: number;
  }>;
  hit_rate_text?: string;
  hit_rate_percent?: number | null;
}

export interface LegendGroup {
  label: string;
  values: string[];
}

export interface LotteryLegend {
  zodiac_numbers: LegendGroup[];
  wuxing_numbers: LegendGroup[];
  wave_numbers: LegendGroup[];
}

export interface ZiliaoRecord {
  id: number;
  type: string;
  period: number;
  title?: string; // 后端生成的预测内容HTML
  effect_values?: string[];
  visual_payload?: {
    mode: string;
    visual: string;
    hit_effect_mode?: 'normal' | 'legacy' | string;
    background_image?: string;
    scene_width?: number;
    scene_height?: number;
    items: Array<{
      name: string;
      value: string;
      display: string;
      hit: boolean;
      predicted?: boolean;
      image?: string;
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    }>;
  } | null;
  tm_num?: string;
  tm_sx?: string;
  is_opened?: boolean;
  suc?: boolean | null; // true=准, false=错, null=未开奖
  open_title?: string;
  open_scope?: string;
}

export interface ZiliaoGroup {
  title: string;
  tag: string;
  play_mode?: 'normal' | 'image' | 'image_text' | string;
  rule?: string; // 玩法规则说明
  rule_details?: LegendGroup[];
  hit_rate_text?: string;
  hit_rate_percent?: number | null;
  records: ZiliaoRecord[];
}

export interface NextDraw {
  period: number;
  is_open: boolean;
  opentime: number;
}

export interface DrawTimeConfig {
  draw_time: string;
  hour: number;
  minute: number;
  second: number;
  timezone?: string;
  game: GameType;
}

export interface LotteryAdItem {
  id: number;
  title: string;
  position: string;
  image: string;
  link_url: string;
}

export interface LotteryAdResponse {
  enabled: boolean;
  fallback_enabled: boolean;
  fallback_tag: string;
  fallback_title: string;
  fallback_subtitle: string;
  list: LotteryAdItem[];
}

export interface LotterySiteSettings {
  site_name: string;
  site_short_name: string;
  site_url: string;
  site_logo: string;
  site_favicon: string;
  site_notice: string;
  site_description: string;
  service_contact: string;
  company_address: string;
  copyright_text: string;
  seo_keywords: string;
  seo_description: string;
}

export interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

export interface LatestDrawResponse {
  prev: {
    format: BallInfo[];
    period: number;
    isopen: number;
    opentime?: number;
    regular_count?: number;
    special_count?: number;
  };
  next: NextDraw;
  is_drawing?: boolean;
}

export type GameType = 'amssc' | 'xamlhc' | 'amlhc' | 'xglhc';

export interface GameInfo {
  code: GameType;
  title: string;
  shortTitle: string;
  /** @deprecated 用 title 替代，保留只为兼容旧调用点 */
  name: string;
  /** @deprecated 用 shortTitle 替代，保留只为兼容旧调用点 */
  shortName: string;
}

function buildGameInfo(code: GameType, title: string, shortTitle: string): GameInfo {
  return { code, title, shortTitle, name: title, shortName: shortTitle };
}

// 兜底默认值：接口失败/未返回时使用，避免首页空白
export const GAMES: GameInfo[] = [
  buildGameInfo('amssc', '澳门新新彩', '新新彩'),
  buildGameInfo('xamlhc', '澳门下半场', '下半场'),
  buildGameInfo('amlhc', '澳门六合彩', '澳六合'),
  buildGameInfo('xglhc', '香港六合彩', '港六合'),
];

let cachedEnabledGames: GameInfo[] | null = null;
const enabledListeners = new Set<() => void>();

export interface EnabledGamePayload {
  code: string;
  title: string;
  shortTitle?: string;
}

export function setEnabledGames(payload: EnabledGamePayload[] | null): void {
  if (payload === null) {
    cachedEnabledGames = null;
  } else {
    const known = new Set(GAMES.map((item) => item.code));
    cachedEnabledGames = payload
      .filter((item): item is EnabledGamePayload & { code: GameType } =>
        known.has(item.code as GameType),
      )
      .map((item) => buildGameInfo(
        item.code as GameType,
        item.title || '',
        (item.shortTitle && item.shortTitle.trim()) || item.title || '',
      ));
  }
  enabledListeners.forEach((cb) => cb());
}

/** @deprecated 使用 setEnabledGames（含 title/shortTitle）替代 */
export function setEnabledGameCodes(codes: string[] | null): void {
  if (codes === null) {
    cachedEnabledGames = null;
  } else {
    const known = new Set(GAMES.map((item) => item.code));
    cachedEnabledGames = GAMES.filter((g) => codes.includes(g.code) && known.has(g.code));
  }
  enabledListeners.forEach((cb) => cb());
}

export function getEnabledGames(): GameInfo[] {
  return cachedEnabledGames ?? GAMES;
}

export function subscribeEnabledGames(listener: () => void): () => void {
  enabledListeners.add(listener);
  return () => {
    enabledListeners.delete(listener);
  };
}
